import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { AxiosInstance } from 'axios';
import api, {
    getSessionInfo,
    initializeCsrfProtection,
    refreshSession,
    SESSION_EXPIRED_EVENT,
    SESSION_EXPIRY_KEY,
    SESSION_RENEWED_EVENT,
    SessionInfo,
} from '@/api/axios';

export interface Secretary {
    id: string;
    name: string;
    itTeam?: { id: string; name: string };
}

export interface ItTeam {
    id: string;
    name: string;
    secretaries: Secretary[];
}

export interface User {
    id: string;
    name: string;
    email: string;
    role: string;
    enabled: boolean;
    secretaries: Secretary[];
    itTeams?: ItTeam[];
}

interface AuthContextType {
    user: User | null;
    activeSecretary: Secretary | null;
    login: (email: string, password: string) => Promise<User>;
    logout: () => Promise<void>;
    changeSecretaryContext: () => void;
    setActiveSecretary: (secretary: Secretary) => void;
    sessionExpiresAt: number | null;
    renewSession: () => Promise<void>;
    loading: boolean;
    api: AxiosInstance;
}

const STORAGE_KEY = '@chamados:activeSecretary';
const AuthContext = createContext<AuthContextType | undefined>(undefined);

const readStoredSecretary = (): Secretary | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const value = JSON.parse(raw);
        return typeof value?.id === 'string' && typeof value?.name === 'string' ? value : null;
    } catch {
        localStorage.removeItem(STORAGE_KEY);
        return null;
    }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeSecretary, setActiveSecretaryState] = useState<Secretary | null>(readStoredSecretary);
    const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);

    const updateSessionExpiration = useCallback((session: SessionInfo) => {
        const expiresAt = Date.parse(session.expiresAt);
        if (!Number.isFinite(expiresAt)) {
            throw new Error('O servidor retornou um vencimento de sessão inválido.');
        }
        setSessionExpiresAt(expiresAt);
        localStorage.setItem(SESSION_EXPIRY_KEY, String(expiresAt));
    }, []);

    const clearClientSession = useCallback(() => {
        setUser(null);
        setActiveSecretaryState(null);
        setSessionExpiresAt(null);
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(SESSION_EXPIRY_KEY);
    }, []);

    const reconcileSecretary = useCallback((loggedUser: User) => {
        const secretaries = loggedUser.secretaries ?? [];
        const stored = readStoredSecretary();
        const validStored = stored ? secretaries.find(item => item.id === stored.id) ?? null : null;

        if (validStored) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(validStored));
            setActiveSecretaryState(validStored);
            return;
        }

        localStorage.removeItem(STORAGE_KEY);
        if (secretaries.length === 1) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(secretaries[0]));
            setActiveSecretaryState(secretaries[0]);
        } else {
            setActiveSecretaryState(null);
        }
    }, []);

    const checkUserSession = useCallback(async (): Promise<User> => {
        const response = await api.get<User>('/auth/me');
        setUser(response.data);
        reconcileSecretary(response.data);
        return response.data;
    }, [reconcileSecretary]);

    useEffect(() => {
        let mounted = true;
        const initialize = async () => {
            try {
                await initializeCsrfProtection();
                await checkUserSession();
                updateSessionExpiration(await getSessionInfo());
            } catch {
                if (mounted) clearClientSession();
            } finally {
                if (mounted) setLoading(false);
            }
        };
        initialize();
        return () => { mounted = false; };
    }, [checkUserSession, clearClientSession, updateSessionExpiration]);

    useEffect(() => {
        let expirationInProgress = false;
        const onExpired = async (event: Event) => {
            if (expirationInProgress) return;

            const detail = (event as CustomEvent<{ expiresAt?: number; force?: boolean }>).detail;
            const eventExpiration = Number(detail?.expiresAt);
            const storedExpiration = Number(localStorage.getItem(SESSION_EXPIRY_KEY));

            // Uma aba em segundo plano pode acordar com um temporizador antigo.
            // Se outra aba já iniciou uma sessão nova, o evento antigo é ignorado.
            if (!detail?.force
                && Number.isFinite(eventExpiration)
                && Number.isFinite(storedExpiration)
                && storedExpiration > Date.now()
                && storedExpiration !== eventExpiration) {
                return;
            }

            expirationInProgress = true;
            const shouldRedirect = !['/', '/login', '/forgot-password', '/reset-password']
                .includes(window.location.pathname);
            clearClientSession();
            try {
                await api.post('/auth/logout', {});
            } catch {
                // A navegação para o login deve ocorrer mesmo sem conexão com a API.
            } finally {
                if (shouldRedirect) {
                    window.location.assign('/?session=expired');
                }
            }
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    }, [clearClientSession]);

    useEffect(() => {
        const onRenewed = (event: Event) => {
            const session = (event as CustomEvent<SessionInfo>).detail;
            if (session?.expiresAt) updateSessionExpiration(session);
        };
        window.addEventListener(SESSION_RENEWED_EVENT, onRenewed);
        return () => window.removeEventListener(SESSION_RENEWED_EVENT, onRenewed);
    }, [updateSessionExpiration]);

    useEffect(() => {
        const syncSessionBetweenTabs = (event: StorageEvent) => {
            if (event.key !== SESSION_EXPIRY_KEY) return;

            if (event.newValue === null) {
                // A remoção também ocorre no início de um novo login. Ela não
                // pode ser interpretada por outra aba como ordem para revogar
                // os cookies compartilhados do navegador.
                setSessionExpiresAt(null);
                return;
            }

            const expiresAt = Number(event.newValue);
            if (Number.isFinite(expiresAt)) {
                setSessionExpiresAt(expiresAt);
            }
        };

        window.addEventListener('storage', syncSessionBetweenTabs);
        return () => window.removeEventListener('storage', syncSessionBetweenTabs);
    }, []);

    const login = async (email: string, password: string): Promise<User> => {
        await initializeCsrfProtection();
        const response = await api.post<SessionInfo>('/auth/login', { email: email.trim(), password });
        updateSessionExpiration(response.data);
        return checkUserSession();
    };

    const renewSession = async (): Promise<void> => {
        updateSessionExpiration(await refreshSession());
    };

    const setActiveSecretary = (secretary: Secretary) => {
        if (!user?.secretaries?.some(item => item.id === secretary.id)) {
            throw new Error('Secretaria não autorizada para este usuário.');
        }
        const current = user.secretaries.find(item => item.id === secretary.id)!;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
        setActiveSecretaryState(current);
    };

    const changeSecretaryContext = () => {
        localStorage.removeItem(STORAGE_KEY);
        setActiveSecretaryState(null);
        window.location.assign('/select-secretary');
    };

    const logout = async () => {
        try {
            await api.post('/auth/logout', {});
        } finally {
            clearClientSession();
            window.location.assign('/');
        }
    };

    return (
        <AuthContext.Provider value={{
            user, activeSecretary, login, logout, changeSecretaryContext,
            setActiveSecretary, sessionExpiresAt, renewSession, loading, api
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth deve ser utilizado dentro de um AuthProvider');
    return context;
};
